import { Icons8IllustrationByIdResponse, getIcons8IllustrationById } from 'libs/e2api/icons8';
import { store, useAppDispatch } from 'modules/Editor/store';
import { selectRecommendedIconPack } from 'modules/Editor/store/editorSettingsSelector';
import { setRecommendedIconPack } from 'modules/Editor/store/editorSettingsSlice';
import { RecommendedIconPack } from 'modules/Editor/store/editorSettingsSlice.types';
import { useCallback } from 'react';
import { useLoaderData } from 'react-router-dom';
import { useSWRConfig } from 'swr';

import { getIcons8PlatformFromIconPath } from '../IconWidgetMenu.helpers';

export type SetRecommendedIconPackFromIconArgs = {
  isMono?: boolean;
  type: 'icons8' | 'icons8Illustration';
  iconId: string;
  iconPath?: string;
};

const ICONS8_ILLUSTRATION_CACHE_PREFIX = 'ICONS8-ILLUSTRATION-STYLE-';

/**
 * Hook to set the recommended icon pack based on the icon data
 *
 * @returns Function to set the recommended icon pack
 */
export const useRecommendedIconPack = () => {
  const dispatch = useAppDispatch();
  const token = useLoaderData() as string;
  const { cache, mutate } = useSWRConfig();

  // Get the Icons8 pack based on the icon path
  const getIcons8Pack = useCallback((iconPath: string, isMono: boolean) => {
    const icons8Platform = iconPath ? getIcons8PlatformFromIconPath(iconPath) : null;
    if (icons8Platform) {
      return {
        filter: icons8Platform,
        type: 'icons8' as const,
        isMono: !!isMono,
      };
    }
    return null;
  }, []);

  // Get the Icons8 illustration pack based on the icon id
  // Must do a fetch to icons8 vector api to find style
  const getIllustrationPack = useCallback(
    async (iconId: string) => {
      let illustrationData = cache?.get(`${ICONS8_ILLUSTRATION_CACHE_PREFIX}${iconId}`)
        ?.data as Icons8IllustrationByIdResponse;
      if (!illustrationData) {
        illustrationData = await getIcons8IllustrationById(
          {
            id: iconId,
            fields: 'styles',
          },
          token,
        );

        // Add to cache to prevent multiple calls for an icon
        mutate(`${ICONS8_ILLUSTRATION_CACHE_PREFIX}${iconId}`, illustrationData);
      }

      if (illustrationData) {
        if (illustrationData.styles?.[0]) {
          return {
            type: 'icons8Illustration' as const,
            filter: illustrationData.styles[0],
            isMono: false,
          };
        }
      }
      return null;
    },
    [cache, token, mutate],
  );

  // Set the recommended icon pack based on the icon data
  // If setToNullIfNotDefined is TRUE, will reset the pack to NULL if the provided icon is not part of a pack
  const setRecommendedIconPackFromIcon = useCallback(
    async (
      { iconId, iconPath, isMono, type = 'icons8' }: SetRecommendedIconPackFromIconArgs,
      setToNullIfNotDefined = true,
    ) => {
      let iconPack: RecommendedIconPack | null = null;
      if (type === 'icons8') {
        iconPack = getIcons8Pack(iconPath!, !!isMono);
      } else if (type === 'icons8Illustration') {
        iconPack = await getIllustrationPack(iconId);
      }

      // Don't reset if same pack
      const currentPack = selectRecommendedIconPack(store.getState());
      if (currentPack?.type === iconPack?.type && currentPack?.filter === iconPack?.filter) {
        return;
      }

      if (!!iconPack) {
        dispatch(setRecommendedIconPack(iconPack));
        return;
      } else if (setToNullIfNotDefined) {
        dispatch(setRecommendedIconPack(null));
      }
    },
    [dispatch, getIcons8Pack, getIllustrationPack],
  );

  return { setRecommendedIconPackFromIcon };
};
